import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, AlignmentType } from "docx";
import jsPDF from "jspdf";
import "../styles/LetterGenerator.css";
import { useAuth } from "../lib/auth"; // Hook d'authentification
import apiClient from "../Api/apiConfig"; // Client API

export default function LetterGenerator() {
  const { isAuthenticated, user } = useAuth(); // Authentification
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState("");
  const [generatedLetter, setGeneratedLetter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFlipped, setIsFlipped] = useState(false);

  // Redirection si non connecté
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  const handleGenerateLetter = async () => {
    if (!prompt.trim()) {
      setError("Veuillez entrer un prompt.");
      return;
    }

    setLoading(true);
    setError("");
    setGeneratedLetter("");
    setIsFlipped(false);

    try {
      const response = await apiClient.post("/api/gemini/generate-letter", {
        prompt,
        user, // Envoi des infos utilisateur
      });

      setGeneratedLetter(response.data.letter);
      setIsFlipped(true);
    } catch (err) {
      setError(
        err.response?.data?.error || "Erreur lors de la génération de la lettre."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadAsDocx = () => {
    const doc = new Document({
      sections: [
        {
          children: [
            // Informations de l'expéditeur
            new Paragraph({
              alignment: AlignmentType.LEFT,
              children: [
                new TextRun(`${user.firstName} ${user.lastName}`),
                new TextRun("\n123 Rue Imaginaire"), // Exemple d'adresse
                new TextRun("\n75000 Paris, France"),
                new TextRun(`\nEmail : ${user.email}`),
              ],
            }),
            new Paragraph({ text: "", spacing: { after: 200 } }), // Espacement

            // Titre
            new Paragraph({
              text: "Lettre Générée",
              alignment: AlignmentType.CENTER,
              heading: "HEADING_1",
              spacing: { after: 300 },
            }),

            // Contenu de la lettre
            new Paragraph({
              text: generatedLetter,
              spacing: { line: 360 },
            }),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, "lettre-générée.docx");
    });
  };

  const downloadAsPDF = () => {
    const pdf = new jsPDF();
    pdf.setFontSize(18);
    pdf.text("Lettre Générée", 105, 20, null, null, "center");
    pdf.setFontSize(12);
    pdf.text(`${user.firstName} ${user.lastName}`, 10, 30);
    pdf.text("123 Rue Imaginaire", 10, 40); // Exemple d'adresse
    pdf.text("75000 Paris, France", 10, 50);
    pdf.text(`Email : ${user.email}`, 10, 60);
    pdf.text(generatedLetter, 10, 80, { maxWidth: 180 });
    pdf.save("lettre-générée.pdf");
  };

  return (
    <div className="letter-generator-container">
      <div className="info-section">
        <h2>Bienvenue dans l'atelier magique de création de courriers assistée par IA ! ✨</h2>
        <p>
        Vous êtes fatigué de passer des heures à rédiger des courriers officiels ou personnels ? Pas de panique, notre Générateur de Courriers est là pour vous sauver la mise ! 🦸‍♂️🦸‍♀️
        </p>
        <h2>Comment ça marche ? 🛠️</h2>
        <p>Exprimez votre besoin : Entrez simplement votre idée, demande, ou sujet dans la zone de saisie (appelée "prompt"). Vous pouvez y mettre autant de détails que nécessaire, ou garder ça simple si vous êtes pressé(e) ! 📝

Laissez la magie opérer : Une fois votre prompt prêt, cliquez sur le bouton "Générer la lettre". À ce moment-là, notre assistant IA ultra-intelligent analyse votre texte et rédige un courrier structuré, professionnel et adapté à votre besoin. 💻✨

Admirez le résultat : Une fois la lettre générée, elle s'affiche comme par magie ! Vous pouvez la lire, la modifier, ou l'enregistrer directement.

Téléchargez votre chef-d'œuvre : Votre courrier est prêt ? Téléchargez-le en format DOCX (Word) ou PDF en un seul clic ! Vous êtes maintenant prêt à l'envoyer à qui de droit. 📤</p>
<h2>Mais ce n'est pas tout ! 🎁</h2>
<p>Personnalisation incluse : Le générateur ajoute automatiquement vos informations personnelles comme votre nom, prénom, adresse et email pour un courrier prêt à l’emploi.
Gagnez du temps : Fini les recherches interminables pour trouver les bonnes formulations ou le format idéal.
Vérifiez facilement vos contenus : Notre outil est une base, mais n’oubliez pas d’apporter une touche personnelle pour qu’il reflète au mieux votre style et votre intention. 🤓</p>

        <h3 className="warning-text">⚠️ Vérifiez toujours vos contenus avant de les utiliser.</h3>
      </div>

      <div className="card-container">
        <div className={`card ${isFlipped ? "flipped" : ""}`}>
          {/* Face avant */}
          <div className="card-front">
            <h2>Générateur de Lettre</h2>
            <textarea
              placeholder="Entrez votre prompt ici..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="prompt-input"
            ></textarea>
            <button
              onClick={handleGenerateLetter}
              disabled={loading}
              className="generate-button"
            >
              {loading ? "Génération en cours..." : "Générer la lettre"}
            </button>
            {error && <p className="error-message">{error}</p>}
          </div>

          {/* Face arrière */}
          <div className="card-back">
            <h2>Lettre Générée</h2>
            <textarea
              readOnly
              value={generatedLetter}
              className="generated-letter large-result"
            ></textarea>
            <div className="download-buttons">
              <button onClick={downloadAsDocx} className="download-button">
                Télécharger en DOCX
              </button>
              <button onClick={downloadAsPDF} className="download-button">
                Télécharger en PDF
              </button>
              <button
                onClick={() => setIsFlipped(false)}
                className="retour-button"
              >
                Retour/Nouveau
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
